/**
 * ===========================================================================
 * boardScope.ts - THE RAIL FOLLOWS THE BOARD (2026-09-25)
 *
 * > Pete, 2026-09-25: *"when you click loading, it sets the filter correctly on
 * > the calendar but it doesn't filter the drivers/client/carrier/consignee and
 * > the right side panels to those appointments"*
 * > and, a minute later: *"I guess that is the same with all the status' too"*.
 *
 * ---- ⛔⛔ THE SECOND MESSAGE IS THE FINDING ------------------------------
 *
 * The rail has NEVER followed the board - not for status, not for warnings, not
 * for the work kind. `Calendar.tsx` handed `CheckInPanel` the RAW day list, so
 * the party cards counted every appointment of the day however the board was
 * filtered, and the three panels scope on a HOVER FOCUS which knows nothing
 * about a filter at all.
 *
 *   ** THE NEW CONTROL DID NOT BREAK THIS. IT ASKED THE FIRST QUESTION THE OLD
 *      CONTROLS NEVER MADE ANYONE ASK. ** Nobody filters by status and then
 *   reads the party cards as a set. "Show me what is being unloaded" is the
 *   first filter whose answer the rail obviously ought to share.
 *
 * ---- ⭐⭐ ONE RULE, FOUR SURFACES -----------------------------------------
 *
 * Driver cards, party cards, Labour, Communications and Orders all ask the same
 * question and it is written here once:
 *
 *   ** A ROW THAT NAMES AN APPOINTMENT MUST NAME ONE THE BOARD IS SHOWING.
 *      A ROW THAT NAMES NONE IS NOT THE BOARD'S TO HIDE. **
 *
 * ⚠️ THE SECOND HALF IS NOT A CONVENIENCE, IT IS THE POINT. Three different
 * things have no appointment and each one matters:
 *
 *   - an UNMATCHED ARRIVAL. Pete chose 2026-09-25 that he always shows. He has
 *     no status and no work kind, so a strict mirror hides him the moment any
 *     filter is on - and he is the one card that needs somebody to act.
 *       ** A TRUCK IN THE YARD WITH NO APPOINTMENT IS THE YARD'S STATE, NOT AN
 *          APPOINTMENT'S. HIDING IT SHOWS A YARD EMPTIER THAN IT IS. **
 *     This is the same exemption `filterAppts` already gives a door block, for
 *     the same reason, recorded in `Calendar.tsx`.
 *
 *   - an UNBOOKED WMS ORDER. The Orders panel exists to FIND orders to book.
 *     Every unbooked order has no appointment, so bounding them by the board
 *     would empty the panel of exactly what it is for the moment a filter is
 *     touched.
 *       ** A PANEL WHOSE PURPOSE IS THE UNMATCHED CANNOT BE SCOPED BY MATCHES. **
 *
 *   - a MESSAGE OR A LABOUR SESSION with no appointment behind it. Rare, and
 *     honest: nothing on the board says anything about it either way.
 *
 * ---- ⛔⛔ WHY THIS IS A SET AND NOT A FILTERED LIST -----------------------
 *
 * ⚠️ READ THIS BEFORE "SIMPLIFYING" IT AWAY. The one-character version of this
 * whole file is `appointments={filterAppts(calendar.appointments)}`, and it is
 * wrong. That prop is not the panel's VIEW: its own docstring says *"Today's
 * appointments, for the attach picker"*, and `ScanFolderPanel`'s says *"what a
 * document can be filed onto"*. It feeds `apptById` - the status word beside
 * every driver's name - plus `attachable` and the signing lookup. Narrow it and
 * a truck whose appointment is filtered off the board can no longer be ATTACHED
 * to it, and its card loses its status word.
 *
 *   ** A FILTER THAT NARROWS WHAT YOU CAN SEE MUST NOT NARROW WHAT YOU CAN DO. **
 *   ** A PROP WHOSE DOCSTRING NAMES A CAPABILITY IS NOT A VIEW, HOWEVER MUCH IT
 *      LOOKS LIKE ONE FROM THE CALL SITE. **
 *
 * So the board's ids ride BESIDE the full list - a second axis, which is the
 * same answer `WorkFilterBar` itself reached about the status row:
 *   ** A SECOND QUESTION ABOUT A ROW IS A SECOND AXIS. **
 * ===========================================================================
 */

/**
 * Is this row's appointment on the board?
 *
 * ⛔ `undefined` IS NOT AN EMPTY SET. An empty Set is a real answer - the filter
 * excluded everything - while `undefined` is the absence of a question, from a
 * caller that has not been given the bound. Collapsing the two would blank every
 * panel in any screen that has not been wired up yet.
 *   ** "NOBODY ASKED" AND "THE ANSWER IS NONE" MUST NOT BE THE SAME VALUE. **
 *
 * ⚠️ IDS ARE COMPARED AS STRINGS, ALWAYS. `AppointmentDto.appointmentId` is a
 * number in some DTOs and a string in others, and `Set<string>.has(55)` is
 * silently false rather than a type error at the boundaries where one of them
 * has been through JSON. `railFocus.ts` converts for the same reason and says
 * so; this does it in the one place instead of at five call sites.
 */
export function onBoard(
    ids: ReadonlySet<string> | undefined,
    appointmentId: string | number | null | undefined,
): boolean {
    if (!ids) return true;
    if (appointmentId === null || appointmentId === undefined || appointmentId === '') return true;
    return ids.has(String(appointmentId));
}

/**
 * ⭐ THE SAME QUESTION FOR A ROW THAT CAN NAME SEVERAL - a WMS order.
 *
 * ⛔⛔ AN ORDER IS BOOKED ON A LIST, NOT ON ONE APPOINTMENT. `OrdersPanelRowDto`
 * carries `bookedOn: [{ appointmentId, appointmentNumber }]`, and `railFocus`'s
 * `ordersMatchesFocus` already reads it as a list for exactly that reason. A
 * scalar test here would have taken the first booking and silently hidden an
 * order split across two appointments whenever the board showed only the second.
 *   ** A FIELD THAT IS A LIST IS A LIST ON EVERY PATH, OR ONE PATH IS A BUG
 *      WAITING FOR THE SECOND ELEMENT. **
 *
 * ⚠️ ANY, NOT ALL. An order part-booked onto something the board is showing IS on
 * the board; requiring every booking would hide it the moment a scheduler
 * narrowed to one of its two appointments, which is when they most want it.
 *
 * ⚠️ AN EMPTY LIST IS "NAMES NONE" AND SURVIVES - see the unbooked-order case in
 * the header. This is the half of the rule the Orders panel depends on entirely.
 */
export function onBoardAny(
    ids: ReadonlySet<string> | undefined,
    appointmentIds: readonly (string | number | null | undefined)[] | null | undefined,
): boolean {
    if (!ids) return true;
    const named = (appointmentIds ?? [])
        .filter(v => v !== null && v !== undefined && v !== '');
    if (named.length === 0) return true;
    return named.some(v => ids.has(String(v)));
}

/**
 * The sentence itself, so three panels cannot word it three ways.
 * ⚠️ RETURNS null AT ZERO rather than an empty string - a caller that renders it
 * unconditionally then draws nothing instead of an empty element with padding.
 */
export function boardScopeNote(hidden: number): string | null {
    if (hidden <= 0) return null;
    return hidden === 1
        ? '1 more on this day is hidden by the board’s filters.'
        : `${hidden} more on this day are hidden by the board’s filters.`;
}
